import React, { useMemo, useState } from "react";
import {
  Chart,
  Title,
  Series,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "@highcharts/react";
import "../../../css/calculators/MortgageOverpaymentCalculator.css";
import SecondaryButton from "../../designSystem/FilledButton";
import PrimaryButton from "../../designSystem/OutlinedButton";
import CustomSelect from "../../designSystem/CustomSelect";
import { parseNumericValue } from "./HelperFunctions/utils";
import FormattedNumberInput from "./HelperFunctions/FormattedNumberInput";


const initialState = {
  mortgageType: "repayment",
  loanSize: 0,
  monthlyOverpayment: 0,
  interestRate: 4.5,
  repaymentPeriod: 25,
};

const formatCurrency = (value, decimals = 0) => {
  return Number(value || 0).toLocaleString("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

const formatNumber = (value, decimals = 0) => {
  return Number(value || 0).toLocaleString("en-GB", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

const cleanNumericInput = (value) => value.replace(/[^\d.]/g, "");

const calculateMonthlyRepayment = (principal, annualRate, years) => {
  const monthlyRate = annualRate / 100 / 12;
  const totalMonths = years * 12;

  if (!principal || !years) return 0;
  if (monthlyRate === 0) return principal / totalMonths;

  return (
    (principal * monthlyRate * Math.pow(1 + monthlyRate, totalMonths)) /
    (Math.pow(1 + monthlyRate, totalMonths) - 1)
  );
};

const formatYearsMonths = (months) => {
  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;

  if (years === 0)
    return `${remainingMonths} month${remainingMonths === 1 ? "" : "s"}`;
  if (remainingMonths === 0) return `${years} year${years === 1 ? "" : "s"}`;

  return `${years} year${years === 1 ? "" : "s"} and ${remainingMonths} month${remainingMonths === 1 ? "" : "s"}`;
};

const buildMortgageSchedule = ({
  mortgageType,
  loanSize,
  annualRate,
  years,
  monthlyOverpayment,
}) => {
  const principal = Number(loanSize || 0);
  const overpayment = Number(monthlyOverpayment || 0);
  const monthlyRate = Number(annualRate || 0) / 100 / 12;
  const totalMonths = Number(years || 0) * 12;

  if (!principal || !years || totalMonths <= 0) return null;

  const standardMonthlyPayment =
    mortgageType === "interestOnly"
      ? monthlyRate === 0
        ? 0
        : principal * monthlyRate
      : calculateMonthlyRepayment(principal, annualRate, years);

  const fixedInterestOnlyTotalPayment =
    mortgageType === "interestOnly" ? standardMonthlyPayment + overpayment : 0;

  const simulateSchedule = (extraPayment = 0) => {
    let balance = principal;
    let totalInterest = 0;
    let totalPaid = 0;
    let paidOffMonth = totalMonths;
    const yearlyBalances = [{ year: 0, balance: principal }];

    for (let month = 1; month <= totalMonths; month += 1) {
      if (balance <= 0) {
        if (month % 12 === 0) {
          yearlyBalances.push({
            year: month / 12,
            balance: 0,
          });
        }
        continue;
      }

      const interest = balance * monthlyRate;
      let payment = 0;
      let principalReduction = 0;

      if (mortgageType === "interestOnly") {
        payment =
          extraPayment > 0
            ? fixedInterestOnlyTotalPayment
            : standardMonthlyPayment;

        if (payment > balance + interest) {
          payment = balance + interest;
        }

        principalReduction = Math.max(0, payment - interest);
      } else {
        payment = standardMonthlyPayment + extraPayment;

        if (payment > balance + interest) {
          payment = balance + interest;
        }

        principalReduction = Math.max(0, payment - interest);
      }

      balance = Math.max(0, balance - principalReduction);
      totalInterest += interest;
      totalPaid += payment;

      if (balance <= 0 && paidOffMonth === totalMonths) {
        paidOffMonth = month;
      }

      if (month % 12 === 0) {
        yearlyBalances.push({
          year: month / 12,
          balance,
        });
      }
    }

    return {
      yearlyBalances,
      totalInterest,
      totalPaid,
      paidOffMonth,
      remainingDebt: balance,
    };
  };

  const withoutOverpayment = simulateSchedule(0);
  const withOverpayment = simulateSchedule(overpayment);

  return {
    standardMonthlyPayment,
    totalMonthlyPayment:
      mortgageType === "interestOnly"
        ? fixedInterestOnlyTotalPayment
        : standardMonthlyPayment + overpayment,

    yearlyWithout: withoutOverpayment.yearlyBalances,
    yearlyWith: withOverpayment.yearlyBalances,

    totalInterestWithout: withoutOverpayment.totalInterest,
    totalInterestWith: withOverpayment.totalInterest,
    interestSaved:
      withoutOverpayment.totalInterest - withOverpayment.totalInterest,

    totalPaidWithout: withoutOverpayment.totalPaid,
    totalPaidWith: withOverpayment.totalPaid,

    remainingDebt: withOverpayment.remainingDebt,

    paidOffMonthWithout: withoutOverpayment.paidOffMonth,
    paidOffMonthWith: withOverpayment.paidOffMonth,

    monthsSaved: withoutOverpayment.paidOffMonth - withOverpayment.paidOffMonth,
  };
};

export default function MortgageOverpaymentCalculator() {
  const [formData, setFormData] = useState(initialState);
  const [hasCalculated, setHasCalculated] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (["interestRate", "repaymentPeriod"].includes(name)) {
      setFormData((prev) => ({
        ...prev,
        [name]: value.replace(/[^\d.]/g, ""),
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  const handleClear = () => {
    setFormData(initialState);
    setHasCalculated(false);
  };

  const handleCalculate = () => {
    setHasCalculated(true);
  };

const results = useMemo(() => {
  if (!hasCalculated) return null;

  return buildMortgageSchedule({
    mortgageType: formData.mortgageType,
    loanSize: parseNumericValue(formData.loanSize),
    annualRate: parseNumericValue(formData.interestRate),
    years: parseNumericValue(formData.repaymentPeriod),
    monthlyOverpayment: parseNumericValue(formData.monthlyOverpayment),
  });
}, [formData, hasCalculated]);

  const chartOptions = useMemo(() => {
    if (!results) return null;

    return {
      chart: {
        type: "line",
        backgroundColor: "#f3f3f3",
        borderRadius: 12,
        height: 360,
      },
      title: {
        text: "Your Mortgage Debt Over Time",
        align: "left",
        style: {
          color: "#000000",
          fontSize: "20px",
          fontWeight: "700",
        },
      },
      credits: {
        enabled: false,
      },
      xAxis: {
        categories: results.yearlyWith.map((item) => item.year),
        title: {
          text: "",
        },
      },
      yAxis: {
        title: {
          text: "",
        },
        labels: {
          formatter: function () {
            return formatCurrency(this.value, 0);
          },
        },
      },
      tooltip: {
        shared: true,
        valuePrefix: "£",
        formatter: function () {
          const points = this.points || [];
          let html = `<b>Year ${this.x}</b><br/>`;

          points.forEach((point) => {
            html += `${point.series.name}: <b>${formatCurrency(point.y, 0)}</b><br/>`;
          });

          return html;
        },
      },
      legend: {
        align: "right",
        verticalAlign: "top",
      },
      plotOptions: {
        series: {
          marker: {
            enabled: true,
            radius: 4,
          },
          lineWidth: 2,
        },
      },
      series: [
        {
          name: "Without Overpayment",
          data: results.yearlyWithout.map((item) =>
            Number(item.balance.toFixed(2)),
          ),
          color: "#2d2d2d",
        },
        {
          name: "With Overpayment",
          data: results.yearlyWith.map((item) =>
            Number(item.balance.toFixed(2)),
          ),
          color: "#73151F",
        },
      ],
    };
  }, [results]);

  const canCalculate =
    formData.mortgageType &&
    formData.loanSize &&
    formData.interestRate &&
    formData.repaymentPeriod;

  return (
    <div className="borrow-calculator-page">
      <div className="borrow-form-wrap">
        <div className="title burgandy">About You</div>

        <div className="borrow-field-group">
          <div className="content burgandy align-left">Mortgage type</div>
          <div className="select-wrapper">
            {/* <select
              name="mortgageType"
              value={formData.mortgageType}
              onChange={handleChange}
              className="borrow-input"
            >
              <option value="">Please select</option>
              <option value="interestOnly">Interest only</option>
              <option value="repayment">Repayment</option>
            </select> */}
            <CustomSelect
              name="mortgageType"
              options={[
                { value: "", label: "Please select" },
                { value: "repayment", label: "Repayment mortgage" },
                { value: "interestOnly", label: "Interest only mortgage" },
              ]}
              value={formData.mortgageType}
              onChange={handleChange}
              placeholder="Please select mortgage type"
            />
          </div>
        </div>

        <div className="borrow-field-group">
          <div className="content burgandy align-left">Loan size (£)</div>
          <FormattedNumberInput
            name="loanSize"
            value={formData.loanSize}
            onChange={(raw) =>
              setFormData((prev) => ({
                ...prev,
                loanSize: raw,
              }))
            }
            className="borrow-input content"
            placeholder="400,000"
          />
        </div>

        <div className="borrow-field-group">
          <div className="content burgandy align-left">Monthly overpayment (£)</div>
          <FormattedNumberInput
            name="monthlyOverpayment"
            value={formData.monthlyOverpayment}
            onChange={(raw) =>
              setFormData((prev) => ({
                ...prev,
                monthlyOverpayment: raw,
              }))
            }
            className="borrow-input content"
            placeholder="2,000"
          />
        </div>

        <div className="borrow-field-group">
          <div className="content burgandy align-left">Interest rate (%)</div>
          <input
            type="text"
            className="borrow-input content"
            inputMode="decimal"
            autoComplete="off"
            name="interestRate"
            value={formData.interestRate}
            onChange={handleChange}
            placeholder="4.5"
            //    onBlur={(e) => {
            //                 const formatted = formatCurrency(
            //                   e.target.value.replace(/,/g, ""),
            //                 );
            //                 setFormData((prev) => ({ ...prev, interestRate: formatted }));
            //               }}
          />
        </div>

        <div className="borrow-field-group">
          <div className="content burgandy align-left">Repayment period (years)</div>
          <input
            type="text"
            className="borrow-input content"
            inputMode="decimal"
            autoComplete="off"
            name="repaymentPeriod"
            //    onBlur={(e) => {
            //                const formatted = formatCurrency(
            //                  e.target.value.replace(/,/g, ""),
            //                );
            //                setFormData((prev) => ({ ...prev, repaymentPeriod: formatted }));
            //              }}
            value={formData.repaymentPeriod}
            onChange={handleChange}
            placeholder="25"
          />
        </div>

        <div
          style={{
            display: "flex",
            gap: 16,
            marginTop: 24,
            justifyContent: "flex-end",
          }}
        >
          <SecondaryButton onClick={handleClear} text="Clear" />
          <PrimaryButton onClick={handleCalculate} text="Calculate" />
        </div>
      </div>

      {results && (
        <div className="borrow-result-wrap result-container">
          <div className="title">Result</div>

          <div className="overpayment-calculator__chart-card">
            <Chart
              containerProps={{
                style: { width: "100%", height: "360px" },
              }}
            >
              <Title>Your Mortgage Debt Over Time</Title>

              <XAxis categories={results.yearlyWith.map((item) => item.year)} />
              <YAxis />

              <Series
                type="line"
                name="Without Overpayment"
                data={results.yearlyWithout.map((item) =>
                  Number(item.balance.toFixed(2)),
                )}
              />
              <Series
                type="line"
                name="With Overpayment"
                data={results.yearlyWith.map((item) =>
                  Number(item.balance.toFixed(2)),
                )}
              />

              <Tooltip />
              <Legend />
            </Chart>
          </div>

          {formData.mortgageType === "repayment" ? (
            <>
              <div className="result-section">
                <div className="content-semibold">
                  Overpaying would save you
                </div>
                <div className="title-semibold">
                  {formatCurrency(results.interestSaved, 0)}
                </div>
                <p className="content">
                  In interest alone, and mean you pay the debt off in full{" "}
                  <strong>{formatYearsMonths(results.monthsSaved)}</strong>{" "}
                  earlier.
                </p>
              </div>

              <div className="result-section">
                <div className="content-semibold">
                  Your total payment over this period would be
                </div>
                <div className="title-semibold">
                  {formatCurrency(results.totalPaidWith, 0)}
                </div>
                <p className="content">
                  While normally repaying{" "}
                  <strong>
                    {formatCurrency(results.standardMonthlyPayment, 0)}
                  </strong>{" "}
                  per month, with a regular overpayment of{" "}
                  <strong>
                    {formatCurrency(
                      Number(formData.monthlyOverpayment || 0),
                      0,
                    )}
                  </strong>
                  , making a total monthly repayment of{" "}
                  <strong>
                    {formatCurrency(results.totalMonthlyPayment, 0)}
                  </strong>
                  , you'd be mortgage free{" "}
                  <strong>{formatYearsMonths(results.monthsSaved)}</strong>{" "}
                  earlier.
                </p>
              </div>
            </>
          ) : (
            <div className="result-section">
              <div className="content-semibold">Your Remaining Debt</div>
              <div className="title-semibold">
                {formatCurrency(results.remainingDebt, 0)}
              </div>
              <p className="content">
                Normally you repay{" "}
                <strong>
                  {formatCurrency(results.standardMonthlyPayment, 0)}
                </strong>{" "}
                per month. If you regularly overpay{" "}
                <strong>
                  {formatCurrency(Number(formData.monthlyOverpayment || 0), 0)}
                </strong>
                , making a total repayment of{" "}
                <strong>
                  {formatCurrency(results.totalMonthlyPayment, 0)}
                </strong>
                , you'd have{" "}
                <strong>{formatCurrency(results.remainingDebt, 0)}</strong>{" "}
                mortgage debt remaining at the end of your term.
              </p>
            </div>
          )}

          <div className="overpayment-calculator__table-wrap">
            <table className="overpayment-calculator__table">
              <thead>
                <tr>
                  <th>Year</th>
                  <th>Without Overpayment</th>
                  <th>With Overpayment</th>
                </tr>
              </thead>
              <tbody>
                {results.yearlyWith.map((item, index) => (
                  <tr key={item.year}>
                    <td>{item.year}</td>
                    <td>
                      {formatCurrency(
                        results.yearlyWithout[index]?.balance || 0,
                        0,
                      )}
                    </td>
                    <td>{formatCurrency(item.balance, 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="result-divider" />

          <div className="result-note">
            <div className="content-semibold">PLEASE NOTE:</div>
            <p className="content">
              This mortgage overpayment calculator is an interactive tool which
              should be used for your guidance only, and must not form part of
              your financial decision making process.
            </p>
            <p className="content">
              Your home may be repossessed if you do not keep up repayments on a
              mortgage or any other debt secured on it.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
